import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Button, Link, Typography } from "@mui/material";
import { PayoutEarningLine } from "../../../common/requests/transactions/endpoints";
import { ROUTES } from "../../../common/constant/routes";
import { formatDate } from "../../../common/constant/dates";
import { C } from "../../../common/theme";
import { formatMoney } from "../../../common/utils/format";
import { WELCOME_VOUCHER, feeLabel, minusMoney } from "../../../common/utils/bringBuyers";
import { PayoutLineTotals, payoutLineTotals, sortPayoutLines } from "./payoutUtils";

const SHOWN = 8;

/** One "label ……… amount" row. */
function Row({ label, value, strong, tone }: { label: React.ReactNode; value: string; strong?: boolean; tone?: string }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1.5, fontSize: 13, color: tone ?? C.ink2, fontWeight: strong ? 700 : 400 }}>
      <Box component="span" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
        {label}
      </Box>
      <Box component="span" sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
        {value}
      </Box>
    </Box>
  );
}

const feeValue = (fee: number, currency: string) => (fee > 0 ? minusMoney(fee, currency) : formatMoney(0, currency));

/** One order: sale, Inflos fee (and why), the welcome voucher when the seller
 * paid one, and what the seller gets. */
function OrderLine({ line, first }: { line: PayoutEarningLine; first: boolean }) {
  const c = line.currency;
  return (
    <Box component="li" sx={{ px: 1.5, py: 1.25, borderTop: first ? "none" : `1px solid ${C.line}`, display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Link component={RouterLink} to={`${ROUTES.SALES}?order=${line.order_id}`} underline="hover" className="mono" sx={{ fontSize: 13 }}>
            {line.order_id.slice(0, 8)}
          </Link>
          {line.created_at && (
            <Box component="span" sx={{ color: C.muted, fontSize: 12.5, ml: 1 }}>
              {formatDate(line.created_at)}
            </Box>
          )}
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
          {formatMoney(line.amount, c)}
        </Typography>
      </Box>
      <Row label="Sale" value={formatMoney(line.sale, c)} />
      <Row label={feeLabel(line.fee_reason, line.fee_rate)} value={feeValue(line.fee, c)} />
      {line.voucher_share > 0 && <Row label={`${WELCOME_VOUCHER} · paid by seller`} value={minusMoney(line.voucher_share, c)} tone={C.coralText} />}
    </Box>
  );
}

/** One line under the request amount saying why it's higher or lower than the
 * usual fee would give; null when no order is from the programme. */
export function programmeNote(t: PayoutLineTotals, currency: string): string | null {
  const parts = [];
  if (t.brought) parts.push(`${t.brought} order${t.brought === 1 ? "" : "s"} from their own buyers at 0% fee`);
  if (t.voucherCount) parts.push(`${t.voucherCount} welcome voucher${t.voucherCount === 1 ? "" : "s"} (${minusMoney(t.vouchers, currency)})`);
  return parts.length ? `Includes ${parts.join(" and ")}.` : null;
}

/** The orders a withdrawal paid out, adding up to the request amount. */
export default function PayoutOrders({ lines, currency }: { lines: PayoutEarningLine[]; currency: string }) {
  const [all, setAll] = React.useState(false);
  const sorted = sortPayoutLines(lines);
  const shown = all ? sorted : sorted.slice(0, SHOWN);
  const t = payoutLineTotals(lines);
  return (
    <>
      <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none", border: `1px solid ${C.line}`, borderRadius: "8px" }}>
        {shown.map((line, i) => (
          <OrderLine key={`${line.order_id}-${i}`} line={line} first={i === 0} />
        ))}
      </Box>
      {sorted.length > SHOWN && (
        <Button onClick={() => setAll((v) => !v)} sx={{ alignSelf: "flex-start", minHeight: 44 }}>
          {all ? "Show fewer" : `Show all ${sorted.length} orders`}
        </Button>
      )}
      <Box sx={{ border: `1px solid ${C.line}`, borderRadius: "8px", px: 1.5, py: 1.25, display: "flex", flexDirection: "column", gap: 0.5, bgcolor: C.head }}>
        <Row label={`Sales · ${t.count} order${t.count === 1 ? "" : "s"}`} value={formatMoney(t.sale, currency)} />
        <Row label="Inflos fees" value={feeValue(t.fee, currency)} />
        {t.vouchers > 0 && <Row label={`${WELCOME_VOUCHER}s · paid by seller`} value={minusMoney(t.vouchers, currency)} tone={C.coralText} />}
        <Row label="To pay" value={formatMoney(t.net, currency)} strong tone={C.ink} />
      </Box>
    </>
  );
}
